import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';

import { dealActivityApi } from './index';
import {
  DealActivityMutation,
  CreateCallActivityVariables,
  UpdateCallActivityVariables,
  DeleteCallActivityVariables,
} from './types';

export function useDealActivityMutations() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isDealActivityCreating }] = useMutation<
    DealActivityMutation,
    CreateCallActivityVariables
  >(dealActivityApi.create, {
    awaitRefetchQueries: true,
    refetchQueries: [dealActivityApi.dealActivities],
  });

  const handleCreateDealActivity = useCallback(
    async (dto: CreateCallActivityVariables['input']) => {
      try {
        const { data } = await onCreate({ variables: { input: dto } });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully created deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong creating deal', { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isDealActivityUpdating }] = useMutation<
    DealActivityMutation,
    UpdateCallActivityVariables
  >(dealActivityApi.update, {
    awaitRefetchQueries: true,
    refetchQueries: [dealActivityApi.dealActivities],
  });

  const handleUpdateDealActivity = useCallback(
    async (dto: UpdateCallActivityVariables['input']) => {
      try {
        const { data } = await onUpdate({ variables: { input: dto } });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully updated deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong updating deal', { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onDelete, { loading: isDealActivityDeleting }] = useMutation<
    DealActivityMutation,
    DeleteCallActivityVariables
  >(dealActivityApi.delete, {
    awaitRefetchQueries: true,
    refetchQueries: [dealActivityApi.dealActivities],
  });

  const handleDeleteDealActivity = useCallback(
    async (dto: DeleteCallActivityVariables['input']) => {
      try {
        const { data } = await onDelete({ variables: { input: dto } });

        if (data?.dealActivity?.result?.error || !data?.dealActivity?.result?.success) {
          showNotification(data?.dealActivity?.result?.error ?? 'Something went wrong', { type: 'error' });
          return null;
        }

        showNotification('Successfully deleted deal dealActivity');

        return data.dealActivity.result.entity!;
      } catch (error) {
        logError(error);
        showNotification('Something went wrong deleting deal', { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  return {
    handleCreateDealActivity,
    handleUpdateDealActivity,
    handleDeleteDealActivity,
    isDealActivityCreating,
    isDealActivityUpdating,
    isDealActivityDeleting,
  };
}
